import { useEffect, useMemo, useState } from 'react';
import taskService from '../../../services/taskService';
import { equipmentService } from '../../../services/equipmentService';
import projectService from '../../../services/projectService';
import { userService } from '../../../services/userService';
import AssignTasksDrawer from '../components/AssignTasksDrawer';
import TaskStatusBadge from '../components/TaskStatusBadge';
import useDebounce from '../../../hooks/useDebounce';
import Select from '../../../shared/components/form/Select';
import { 
  User, 
  Calendar, 
  Package2, 
  Clock, 
  CheckCircle2,
  XCircle,
  Search,
  Filter,
  Grid3X3,
  List,
  RefreshCw
} from 'lucide-react';
import { toast } from 'react-toastify';
import { invokeSkill } from '../../../services/brainService';
import BrainSuggestionCard from '../../brain/BrainSuggestionCard';

interface CheckpointTask {
  _id: string;
  checkpointName: string;
  frequency: string;
  status: string;
  assignedEngineerId?: {
    _id: string;
    name: string;
  } | null;
  dueDate: string;
  isOverdue: boolean;
  priority?: 'Low' | 'Medium' | 'High' | 'Critical';
  specification?: string;
  projectId?: string;
  equipmentId?: string;
  assignmentHistory?: Array<{
    user: {
      _id: string;
      name: string;
    };
    assignedBy: {
      _id: string;
      name: string;
    };
    assignmentDate: string;
    reason?: string;
  }>;
}

interface EquipmentWithTasks {
  _id: string;
  equipmentName: string;
  equipmentId: string;
  siteLabel?: string;
  wtgId?: string;
  unassignedTasksCount: number;
  totalTasksCount: number;
  checkpointGroups: Record<string, CheckpointTask[]>;
}


interface TaskFilters {
  projectId: string;
  equipmentIds: string[];
  resourceIds: string[];
  status: string[];
  priority: string[];
  frequency: string[];
  dateRange: {
    from: string;
    to: string;
  };
  searchTerm: string;
}

interface ViewMode {
  type: 'grid' | 'list' | 'kanban';
  showCompleted: boolean;
  groupBy: 'equipment' | 'status' | 'resource' | 'frequency';
}

export default function Tasks() {
  // Core state
  const [projects, setProjects] = useState<any[]>([]);
  const [equipments, setEquipments] = useState<EquipmentWithTasks[]>([]);
  const [resources, setResources] = useState<any[]>([]);
  const [allTasks, setAllTasks] = useState<CheckpointTask[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Assignment drawer state
  const [showAssignmentDrawer, setShowAssignmentDrawer] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<CheckpointTask[]>([]);
  
  // Enhanced filtering state
  const [filters, setFilters] = useState<TaskFilters>({
    projectId: '',
    equipmentIds: [],
    resourceIds: [],
    status: [],
    priority: [],
    frequency: [],
    dateRange: { from: '', to: '' },
    searchTerm: ''
  });
  
  // View and UI state
  const [viewMode, setViewMode] = useState<ViewMode>({
    type: 'grid',
    showCompleted: false,
    groupBy: 'equipment'
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [resourceHint, setResourceHint] = useState<string>('');
  const [resourceHintLoading, setResourceHintLoading] = useState(false);
  
  const debouncedSearchTerm = useDebounce(filters.searchTerm, 300);

  // Comprehensive task filtering
  const filteredTasks = useMemo(() => {
    return allTasks.filter(task => {
      // Project filter
      if (filters.projectId && task.projectId !== filters.projectId) return false;
      
      // Equipment filter
      if (filters.equipmentIds.length > 0 && task.equipmentId && !filters.equipmentIds.includes(task.equipmentId)) return false;
      
      // Resource filter
      if (filters.resourceIds.length > 0) {
        const assignedToResource = task.assignedEngineerId && filters.resourceIds.includes(task.assignedEngineerId._id);
        if (!assignedToResource) return false;
      }
      
      // Status filter
      if (filters.status.length > 0 && !filters.status.includes(task.status)) return false;
      
      // Priority filter
      if (filters.priority.length > 0 && task.priority && !filters.priority.includes(task.priority)) return false;
      
      // Frequency filter
      if (filters.frequency.length > 0 && !filters.frequency.includes(task.frequency)) return false;
      
      // Date range filter
      if (filters.dateRange.from || filters.dateRange.to) {
        const taskDate = new Date(task.dueDate);
        if (filters.dateRange.from && taskDate < new Date(filters.dateRange.from)) return false;
        if (filters.dateRange.to && taskDate > new Date(filters.dateRange.to)) return false;
      }
      
      // Search filter
      if (debouncedSearchTerm) {
        const searchLower = debouncedSearchTerm.toLowerCase();
        const matchesSearch = task.checkpointName.toLowerCase().includes(searchLower) ||
                            (task.specification && task.specification.toLowerCase().includes(searchLower)) ||
                            (task.assignedEngineerId && task.assignedEngineerId.name.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }
      
      // Show completed filter
      if (!viewMode.showCompleted && (task.status === 'pass' || task.status === 'fail')) return false;
      
      return true;
    });
  }, [allTasks, filters, debouncedSearchTerm, viewMode.showCompleted]);

  // Group tasks by selected grouping method
  const groupedTasks = useMemo(() => {
    const groups: Record<string, CheckpointTask[]> = {};
    
    filteredTasks.forEach(task => {
      let groupKey = '';
      
      switch (viewMode.groupBy) {
        case 'equipment':
          groupKey = task.equipmentId || 'unassigned';
          break;
        case 'status':
          groupKey = task.status;
          break;
        case 'resource':
          groupKey = task.assignedEngineerId?._id || 'unassigned';
          break;
        case 'frequency':
          groupKey = task.frequency;
          break;
        default:
          groupKey = 'all';
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(task);
    });
    
    return groups;
  }, [filteredTasks, viewMode.groupBy]);

  // Effects
  useEffect(() => {
    fetchProjects();
    fetchResources();
  }, []);

  useEffect(() => {
    if (filters.projectId) {
      fetchTasks();
      fetchEquipments();
    } else {
      setAllTasks([]);
      setEquipments([]);
    }
  }, [filters.projectId]);

  // Reset state when project changes
  useEffect(() => {
    setSelectedTasks([]);
    setShowAssignmentDrawer(false);
  }, [filters.projectId]);

  // API Functions
  async function fetchProjects() {
    try {
      const res = await projectService.getProjects({ limit: 100, page: 1 });
      setProjects(res.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Failed to load projects');
    }
  }

  async function fetchResources() {
    try {
      const res = await userService.getUsers({ 
        role: 'site_engineer,site_supervisor', 
        status: 'active',
        limit: 100, 
        page: 1 
      });
      setResources(res.users || []);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      toast.error('Failed to load resources');
    }
  }

  async function fetchTasks() {
    setLoading(true);
    try {
      const res = await taskService.getTasks({
        projectId: filters.projectId,
        limit: 1000,
        page: 1,
        sortBy: 'dueDate:asc'
      });
      
      const tasks = Array.isArray(res?.data?.docs) ? res.data.docs : 
                   Array.isArray(res?.data) ? res.data : [];
      
      const formattedTasks: CheckpointTask[] = tasks.map((task: any) => ({
        _id: task._id,
        checkpointName: task.checkpointName || task.checkpointDetails?.name || 'Unnamed Checkpoint',
        frequency: task.frequency || task.checkpointDetails?.frequency || 'Other',
        status: task.status || 'unassigned',
        assignedEngineerId: task.assignedEngineerId,
        dueDate: task.dueDate || task.startDate || task.plannedDate,
        isOverdue: task.isOverdue || false,
        priority: task.priority || 'Medium',
        specification: task.specification || '',
        projectId: task.projectId,
        equipmentId: task.equipmentId,
        assignmentHistory: task.assignedTo?.history || []
      }));
      
      setAllTasks(formattedTasks);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }

  async function fetchEquipments() {
    try {
      const equipmentList = await equipmentService.getEquipment({ 
        projectId: filters.projectId, 
        limit: 500, 
        page: 1 
      });
      const equipmentArray = Array.isArray(equipmentList?.data) ? equipmentList.data : 
                            Array.isArray(equipmentList) ? equipmentList : [];

      setEquipments(equipmentArray);
    } catch (error) {
      console.error('Failed to fetch equipments:', error);
      toast.error('Failed to load equipments');
    }
  }

  // Helper Functions
  const projectOptions = useMemo(() => (
    projects.map((p: any) => ({ value: String(p._id || p.id), label: p.projectName || p.name || 'Untitled Project' }))
  ), [projects]);

  const equipmentOptions = useMemo(() => (
    equipments.map((e: any) => ({ value: String(e._id), label: `${e.equipmentName} (${e.equipmentId})` }))
  ), [equipments]);

  const resourceOptions = useMemo(() => (
    resources.map((r: any) => ({ value: String(r._id), label: `${r.name} (${r.role?.name || 'Engineer'})` }))
  ), [resources]);

  const statusOptions = [
    { value: 'unassigned', label: 'Unassigned' },
    { value: 'due', label: 'Due' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'inprogress', label: 'In Progress' },
    { value: 'hold', label: 'On Hold' },
    { value: 'pass', label: 'Completed' },
    { value: 'fail', label: 'Failed' }
  ];

  const priorityOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Critical', label: 'Critical' }
  ];

  const frequencyOptions = [
    { value: 'Daily', label: 'Daily' },
    { value: 'Weekly', label: 'Weekly' },
    { value: 'Monthly', label: 'Monthly' },
    { value: 'Quarterly', label: 'Quarterly' },
    { value: 'Yearly', label: 'Yearly' }
  ];

  // Filter update functions
  const updateFilter = (key: keyof TaskFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      projectId: '',
      equipmentIds: [],
      resourceIds: [],
      status: [],
      priority: [],
      frequency: [],
      dateRange: { from: '', to: '' },
      searchTerm: ''
    });
  };

  // Task selection handlers
  const handleTaskSelection = (task: CheckpointTask, isSelected: boolean) => {
    if (isSelected) {
      setSelectedTasks(prev => [...prev.filter(t => t._id !== task._id), task]);
    } else {
      setSelectedTasks(prev => prev.filter(t => t._id !== task._id));
    }
  };

  const handleAssignTasks = () => {
    if (selectedTasks.length === 0) {
      toast.warning('Please select at least one task to assign');
      return;
    }
    setShowAssignmentDrawer(true);
  };

  const onAssignmentComplete = () => {
    setSelectedTasks([]);
    setShowAssignmentDrawer(false);
    // Refresh tasks to get updated assignment data
    fetchTasks();
    toast.success('Tasks assigned successfully');
  };

  const loadResourceCoachSuggestion = async () => {
    if (!filters.projectId) {
      toast.info('Select project first');
      return;
    }
    setResourceHintLoading(true);
    try {
      const out = await invokeSkill('resourceCoach', { projectId: filters.projectId });
      setResourceHint(
        typeof out.structured === 'object'
          ? JSON.stringify(out.structured, null, 2)
          : out.assistantMessage || 'No suggestion'
      );
    } catch (e) {
      setResourceHint(e instanceof Error ? e.message : 'Failed to get resource suggestion');
    } finally {
      setResourceHintLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Task Assignment</h1>
              <p className="text-gray-600 mt-2">Select a project, view equipment with unassigned tasks, and assign them to site engineers</p>
            </div>
            {selectedTasks.length > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">{selectedTasks.length} tasks selected</span>
                <button
                  onClick={() => setSelectedTasks([])}
                  className="px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                >
                  Clear Selection
                </button>
                <button
                  onClick={() => handleAssignTasks()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Assign Selected
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Filter Panel */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Task Management</h2>
              <p className="text-gray-600 mt-1">Filter and manage tasks across projects, equipment, and resources</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode(prev => ({ ...prev, type: 'grid' }))}
                  className={`p-2 rounded-lg ${viewMode.type === 'grid' ? 'bg-blue-100 text-blue-700' : 'text-gray-500'}`}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode(prev => ({ ...prev, type: 'list' }))}
                  className={`p-2 rounded-lg ${viewMode.type === 'list' ? 'bg-blue-100 text-blue-700' : 'text-gray-500'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Quick Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Project</label>
              <Select
                options={projectOptions}
                value={filters.projectId}
                onChange={(opt) => updateFilter('projectId', opt?.value || '')}
                placeholder="Select project"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={filters.searchTerm}
                  onChange={(e) => updateFilter('searchTerm', e.target.value)}
                  placeholder="Search tasks..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                multiple
                value={filters.status}
                onChange={(e) => {
                  const selectedValues = Array.from(e.target.selectedOptions, option => option.value);
                  updateFilter('status', selectedValues);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                size={3}
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Group By</label>
              <select
                value={viewMode.groupBy}
                onChange={(e) => setViewMode(prev => ({ ...prev, groupBy: e.target.value as any }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="equipment">Equipment</option>
                <option value="status">Status</option>
                <option value="resource">Resource</option>
                <option value="frequency">Frequency</option>
              </select>
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="border-t border-gray-200 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Equipment</label>
                  <select
                    multiple
                    value={filters.equipmentIds}
                    onChange={(e) => {
                      const selectedValues = Array.from(e.target.selectedOptions, option => option.value);
                      updateFilter('equipmentIds', selectedValues);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    size={3}
                  >
                    {equipmentOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Resources</label>
                  <select
                    multiple
                    value={filters.resourceIds}
                    onChange={(e) => {
                      const selectedValues = Array.from(e.target.selectedOptions, option => option.value);
                      updateFilter('resourceIds', selectedValues);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    size={3}
                  >
                    {resourceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <select
                    multiple
                    value={filters.priority}
                    onChange={(e) => {
                      const selectedValues = Array.from(e.target.selectedOptions, option => option.value);
                      updateFilter('priority', selectedValues);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    size={3}
                  >
                    {priorityOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                  <select
                    multiple
                    value={filters.frequency}
                    onChange={(e) => {
                      const selectedValues = Array.from(e.target.selectedOptions, option => option.value);
                      updateFilter('frequency', selectedValues);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    size={3}
                  >
                    {frequencyOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                  <input
                    type="date"
                    value={filters.dateRange.from}
                    onChange={(e) => updateFilter('dateRange', { ...filters.dateRange, from: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                  <input
                    type="date"
                    value={filters.dateRange.to}
                    onChange={(e) => updateFilter('dateRange', { ...filters.dateRange, to: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={viewMode.showCompleted}
                      onChange={(e) => setViewMode(prev => ({ ...prev, showCompleted: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Show completed tasks</span>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Clear Filters
                  </button>
                  <button
                    onClick={() => fetchTasks()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Task Display */}
        {filters.projectId && (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Tasks</h2>
                  <p className="text-gray-600 mt-1">
                    {loading ? 'Loading tasks...' : 
                     `${filteredTasks.length} tasks found${filteredTasks.length !== allTasks.length ? ` (filtered from ${allTasks.length})` : ''}`}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={loadResourceCoachSuggestion}
                    disabled={resourceHintLoading}
                    className="px-3 py-2 text-xs font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
                  >
                    {resourceHintLoading ? 'Loading AI...' : 'resourceCoach suggestion'}
                  </button>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span>Unassigned</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span>Due</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span>Completed</span>
                    </div>
                  </div>
                  {selectedTasks.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{selectedTasks.length} selected</span>
                      <button
                        onClick={() => setSelectedTasks([])}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => handleAssignTasks()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        Assign Selected
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {resourceHint ? (
              <div className="px-6 pt-4">
                <BrainSuggestionCard title="resourceCoach" body={resourceHint} onAccept={() => {}} onReject={() => {}} onEdit={() => {}} />
              </div>
            ) : null}
            
            <div className="p-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading tasks...</span>
                </div>
              ) : filteredTasks.length === 0 ? (
                <div className="text-center py-12">
                  <Package2 className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No tasks found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {filters.projectId ? 'No tasks match your current filters.' : 'Select a project to view tasks.'}
                  </p>
                </div>
              ) : (
                <TaskDisplay 
                  tasks={filteredTasks}
                  groupedTasks={groupedTasks}
                  viewMode={viewMode}
                  selectedTasks={selectedTasks}
                  onTaskSelect={handleTaskSelection}
                  onAssignTasks={handleAssignTasks}
                />
              )}
            </div>
          </div>
        )}

        {/* Assignment Drawer */}
        <AssignTasksDrawer
          open={showAssignmentDrawer}
          onClose={() => setShowAssignmentDrawer(false)}
          preselectedTaskIds={selectedTasks.map(t => t._id)}
          onAssigned={onAssignmentComplete}
        />
      </div>
    </div>
  );
}

// Task Display Component
function TaskDisplay({ 
  tasks, 
  groupedTasks, 
  viewMode, 
  selectedTasks, 
  onTaskSelect, 
  onAssignTasks 
}: {
  tasks: CheckpointTask[];
  groupedTasks: Record<string, CheckpointTask[]>;
  viewMode: ViewMode;
  selectedTasks: CheckpointTask[];
  onTaskSelect: (task: CheckpointTask, isSelected: boolean) => void;
  onAssignTasks: () => void;
}) {
  if (viewMode.type === 'grid') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tasks.map((task) => {
          const isSelected = selectedTasks.some(t => t._id === task._id);
          const isUnassigned = !task.assignedEngineerId;
          
          return (
            <div 
              key={task._id} 
              className={`border rounded-lg p-4 transition-all hover:shadow-md ${
                isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
              } ${isUnassigned ? 'border-l-4 border-l-red-500' : ''}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => onTaskSelect(task, e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <TaskStatusBadge status={task.status} isOverdue={task.isOverdue} />
                </div>
                {task.priority && (
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    task.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                    task.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                    task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {task.priority}
                  </span>
                )}
              </div>
              
              <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{task.checkpointName}</h3>
              
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{task.frequency}</span>
                </div>
                
                {task.assignedEngineerId ? (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>Assigned to: {task.assignedEngineerId.name}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-600">
                    <XCircle className="w-4 h-4" />
                    <span>Unassigned</span>
                  </div>
                )}
              </div>
              
              {task.specification && (
                <p className="text-sm text-gray-500 mt-3 line-clamp-2">{task.specification}</p>
              )}
              
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  {isUnassigned && (
                    <button
                      onClick={() => onAssignTasks()}
                      className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      Assign
                    </button>
                  )}
                </div>
                <div className="text-xs text-gray-400">
                  {task.assignmentHistory && task.assignmentHistory.length > 1 && (
                    <span>Reassigned {task.assignmentHistory.length - 1} times</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-4">
      {Object.entries(groupedTasks).map(([groupKey, groupTasks]) => (
        <div key={groupKey} className="border border-gray-200 rounded-lg">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 rounded-t-lg">
            <h3 className="font-semibold text-gray-900 capitalize">
              {viewMode.groupBy === 'resource' ? 
                (groupTasks[0]?.assignedEngineerId?.name || 'Unassigned') :
                groupKey
              } ({groupTasks.length} tasks)
            </h3>
          </div>
          
          <div className="divide-y divide-gray-200">
            {groupTasks.map((task) => {
              const isSelected = selectedTasks.some(t => t._id === task._id);
              const isUnassigned = !task.assignedEngineerId;
              
              return (
                <div 
                  key={task._id} 
                  className={`p-4 transition-colors ${isUnassigned ? 'bg-red-50' : 'bg-white'} ${
                    isSelected ? 'ring-2 ring-blue-500' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => onTaskSelect(task, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{task.checkpointName}</h4>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
                          <span>Frequency: {task.frequency}</span>
                          {task.priority && <span>Priority: {task.priority}</span>}
                        </div>
                        {task.assignedEngineerId ? (
                          <div className="flex items-center gap-1 mt-1 text-green-600">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-sm">Assigned to {task.assignedEngineerId.name}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 mt-1 text-red-600">
                            <XCircle className="w-4 h-4" />
                            <span className="text-sm">Unassigned</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <TaskStatusBadge status={task.status} isOverdue={task.isOverdue} />
                      {isUnassigned && (
                        <button
                          onClick={() => onAssignTasks()}
                          className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          Assign
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
